import React from 'react';
import App from 'common/backbone-app';
import { vector, hashMap } from 'mori';
import ReactDialog from 'src/components/common/react-dialog';
import apiUrl from 'common/api-url';
import { DialogFooter } from 'components/common/DialogFooter';
import { Button } from 'components/common/Button';
import Loader from 'components/common/Loader';
import { Time, TIME_TYPES } from 'components/common/DataFormat';
import DialogErrorList from 'src/components/common/dialog/error-list';
import SummaryRowsView from 'src/components/reports/transactions/common/transaction-summary/row';
import OverviewCustomerSection from 'src/components/reports/transactions/common/transaction-summary/overview-customer-section';
import {
  CustomerInfo,
  fetchCustomerInfo as fetchCustomerInfoRequest,
} from 'services/pos-customer-info';
import { fetchQuote as fetchQuoteRequest, voidQuote } from 'services/pos-quote';
import {
  QuoteSummaryData,
  isVoidQuote,
  isUsableVoidPatchResponse,
  normalizeQuoteVoidFields,
  resolveVoidedQuoteDataAfterSuccess,
} from 'services/quote-daily-report';
import { getBannerMessage, getLangs, getQuoteSummaryDisplay } from './quote-summary-display';

const DIALOG_WIDTH = 480;

interface QuoteSummaryDialogProps {
  quoteId: number;
  onClose: () => void;
  quoteData?: QuoteSummaryData;
  onVoidSuccess?: (voidedQuoteData: QuoteSummaryData) => void;
}

interface QuoteSummaryDialogState {
  data: QuoteSummaryData | null;
  customerInfo: CustomerInfo | null;
  failed: boolean;
  isVoiding: boolean;
  voidError: string | null;
}

export class QuoteSummaryDialog extends React.Component<
  QuoteSummaryDialogProps,
  QuoteSummaryDialogState
> {
  private voidQuoteInProgress = false;

  constructor(props: QuoteSummaryDialogProps) {
    super(props);

    this.state = {
      data: props.quoteData ? normalizeQuoteVoidFields(props.quoteData) : null,
      customerInfo: null,
      failed: false,
      isVoiding: false,
      voidError: null,
    };
  }

  setQuoteData = (data: QuoteSummaryData) => {
    this.setState({ data: normalizeQuoteVoidFields(data) });
  };

  getDisplayQuoteData = (): QuoteSummaryData | null => {
    const { data } = this.state;
    if (data) {
      return data;
    }

    const { quoteData } = this.props;
    return quoteData ? normalizeQuoteVoidFields(quoteData) : null;
  };

  loadCustomerInfo = (venueCustomerId?: number) => {
    const setCustomerInfo = (customerInfo: CustomerInfo) => {
      this.setState({ customerInfo });
    };

    if (!venueCustomerId) {
      setCustomerInfo({});
      return;
    }

    fetchCustomerInfoRequest(venueCustomerId)
      .done(setCustomerInfo)
      .fail(() => setCustomerInfo({}));
  };

  fetchQuote = () => {
    const { quoteId } = this.props;

    fetchQuoteRequest(quoteId)
      .done((data: QuoteSummaryData) => {
        this.setQuoteData(data);
        this.loadCustomerInfo(data.venueCustomerId);
      })
      .fail(() => {
        this.setState({ failed: true });
      });
  };

  componentDidMount() {
    const { quoteData } = this.props;

    if (quoteData) {
      this.setQuoteData(quoteData);
      this.loadCustomerInfo(quoteData.venueCustomerId);
      return;
    }

    this.fetchQuote();
  }

  handleOpenPdf = () => {
    const { quoteId } = this.props;

    window.open(apiUrl('POS_QUOTE_PDF', { quoteId }), '_blank');
  };

  endVoiding = (voidError: string | null = null) => {
    this.voidQuoteInProgress = false;
    this.setState({ isVoiding: false, voidError });
  };

  handleVoidQuoteClick = () => {
    const { quoteId } = this.props;
    const { data } = this.state;
    const { quote } = getLangs();

    if (!data || this.voidQuoteInProgress) {
      return;
    }

    this.voidQuoteInProgress = true;
    this.setState({ isVoiding: true, voidError: null });

    const employeeId = App.config.getAccountEmployeeId() ?? data.employeeId;

    if (!employeeId && !App.isMasquerading()) {
      this.endVoiding(quote.voidError);
      return;
    }

    voidQuote(quoteId, employeeId)
      .done((response?: QuoteSummaryData) => {
        try {
          this.handleVoidQuoteSuccess(response);
        } catch {
          this.refetchQuoteAfterVoid();
        }
      })
      .fail(() => {
        this.endVoiding(quote.voidError);
      });
  };

  completeVoidSuccess = (voidedData: QuoteSummaryData) => {
    this.voidQuoteInProgress = false;
    this.setState({
      isVoiding: false,
      voidError: null,
      data: voidedData,
    });

    this.props.onVoidSuccess?.(voidedData);
  };

  refetchQuoteAfterVoid = () => {
    const { quoteId } = this.props;
    const { quote } = getLangs();
    const { data: currentData } = this.state;

    fetchQuoteRequest(quoteId)
      .done((data: QuoteSummaryData) => {
        try {
          if (!currentData) {
            const voidedData = normalizeQuoteVoidFields(data);

            if (!isVoidQuote(voidedData)) {
              this.endVoiding(quote.voidError);
              return;
            }

            this.completeVoidSuccess(voidedData);
            return;
          }

          this.completeVoidSuccess(resolveVoidedQuoteDataAfterSuccess(currentData, data));
        } catch {
          this.endVoiding(quote.voidError);
        }
      })
      .fail(() => {
        this.endVoiding(quote.voidError);
      });
  };

  handleVoidQuoteSuccess = (response?: QuoteSummaryData) => {
    const { data: currentData } = this.state;

    if (!currentData || !isUsableVoidPatchResponse(response)) {
      this.refetchQuoteAfterVoid();
      return;
    }

    this.completeVoidSuccess(resolveVoidedQuoteDataAfterSuccess(currentData, response));
  };

  renderContent() {
    const { data, customerInfo, failed, isVoiding, voidError } = this.state;
    const { quote, voidEntry } = getLangs();

    if (failed) {
      return <div className="loading-error">{quote.loadingError}</div>;
    }

    if (!data || customerInfo === null) {
      return <div className="loading">{quote.loading}</div>;
    }

    const { isVoidTableEntry, labels } = getQuoteSummaryDisplay(data);
    const reference = `${quote.reference}${data.transactionId}`;
    const bannerMessage = getBannerMessage(data);
    const quoteCanBeVoided = !isVoidQuote(data);

    const time = data.venueLocalDateTime ?? data.created;
    // No zone conversion needed for quote/void rows since venueLocalDateTime is already converted
    const timezone = data.venueLocalDateTime == null ? App.getTimezone() : undefined;

    return (
      <>
        {voidError && <DialogErrorList errors={[{ text: voidError }]} />}
        {bannerMessage && (
          <div className="linked-transaction">
            <span>{bannerMessage}</span>
          </div>
        )}
        <div className="overview-summary">
          <div className="left">
            <span className="transaction-id">{reference}</span>
            <span className="overview-row">
              <span className="date-label">{quote.dateLabel}</span>
              <span className="date">
                <Time type={TIME_TYPES.DATE_TIME} time={time} timezone={timezone} />
              </span>
            </span>
            <span className="overview-row">
              <span className="employee-label">{labels.employeeLabel}</span>
              <span className="employee-name">{data.employeeName}</span>
            </span>
          </div>
          <OverviewCustomerSection
            walkInLabel={quote.walkInClient}
            customerName={data.walkIn ? undefined : data.venueCustomerName}
            phone={customerInfo.phone}
            emailAddress={customerInfo.emailAddress}
          />
        </div>

        <div className="service-section">
          {data.serviceLineItems.map((item, index) => (
            <SummaryRowsView
              key={`${item.primaryServiceLineItemConstituentId}-${index}`}
              name={item.name}
              totalPrice={item.originalAmount}
              constituents={
                item.serviceLineItemConstituents?.length
                  ? vector(
                      ...item.serviceLineItemConstituents.map((c) =>
                        hashMap('id', c.id, 'name', c.name),
                      ),
                    )
                  : undefined
              }
            />
          ))}
        </div>

        <div className="transaction-dialog-pricing-section">
          <SummaryRowsView
            name={labels.totalLabel}
            totalPrice={Math.abs(data.grossAmount)}
            additionalClass="total"
            negative={isVoidTableEntry}
          />
        </div>

        <div className="footer">
          <DialogFooter fullWidth>
            <div className="quote-summary-button-wrapper">
              <Button
                dataTest={labels.copyDataTest}
                variant="hollow"
                colour="help"
                label={labels.copyLabel}
                onClick={this.handleOpenPdf}
              />
              {quoteCanBeVoided && (
                <Button
                  dataTest="quote-summary-void-quote"
                  variant="secondary"
                  colour="alert"
                  label={voidEntry.voidQuote}
                  onClick={this.handleVoidQuoteClick}
                  disabled={isVoiding}
                />
              )}
            </div>
          </DialogFooter>
        </div>
      </>
    );
  }

  render() {
    const { onClose } = this.props;
    const { labels } = getQuoteSummaryDisplay(this.getDisplayQuoteData());
    const { isVoiding } = this.state;

    return (
      <>
        <ReactDialog
          dataTest={labels.dialogDataTest}
          title={labels.dialogTitle}
          classes={{ 'checkout-summary': true, 'quote-summary': true }}
          width={DIALOG_WIDTH}
          onClose={onClose}
        >
          {this.renderContent()}
        </ReactDialog>
        {isVoiding && (
          <div data-test="void-quote-loading-modal" className="checkout-summary loading-modal">
            <Loader />
          </div>
        )}
      </>
    );
  }
}

export default QuoteSummaryDialog;
